import { cn } from "@repo/lib";
import useEmblaCarousel from "embla-carousel-react";
import { createContext, useContext, useMemo } from "react";
import type { EmblaCarouselType } from "embla-carousel";

interface CarouselContextProps {
  emblaRef: (node: HTMLElement | null) => void;
  emblaApi?: EmblaCarouselType;
}

const CarouselContext = createContext<CarouselContextProps | null>(null);

const useCarouselContext = (): CarouselContextProps => {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("Carousel components must be used within CarouselRoot");
  }
  return context;
};

interface CarouselRootProps {
  children: JSX.Element | JSX.Element[];
  loop?: boolean;
  align?: "start" | "center" | "end";
}

export const CarouselRoot = ({
  children,
  loop = false,
  align = "center",
}: CarouselRootProps): JSX.Element => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop,
    align,
  });

  const contextValue = useMemo(() => ({ emblaRef, emblaApi }), [emblaRef, emblaApi]);

  return (
    <CarouselContext.Provider value={contextValue}>{children}</CarouselContext.Provider>
  );
};

interface CarouselContainerProps {
  children: JSX.Element | JSX.Element[];
  className?: string;
}

export const CarouselContainer = ({
  children,
  className,
}: CarouselContainerProps): JSX.Element => {
  const { emblaRef } = useCarouselContext();

  return (
    <div ref={emblaRef} className={cn("overflow-hidden z-1 w-full", className)}>
      {children}
    </div>
  );
};

interface CarouselContentProps {
  children: JSX.Element | JSX.Element[];
  className?: string;
}

export const CarouselContent = ({
  children,
  className,
}: CarouselContentProps): JSX.Element => {
  return <div className={cn("flex flex-nowrap gap-x-2 z-1", className)}>{children}</div>;
};

interface CarouselItemProps {
  children: JSX.Element | JSX.Element[];
  className?: string;
}

export const CarouselItem = ({ children, className }: CarouselItemProps): JSX.Element => {
  const { emblaApi } = useCarouselContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (!emblaApi) return;
    const index = emblaApi.selectedScrollSnap();
    const nodes = emblaApi.slideNodes();

    const thisNode = e.currentTarget;
    const clickedIndex = nodes.findIndex((n) => n === thisNode);

    if (clickedIndex > index || clickedIndex < index) {
      emblaApi.scrollTo(clickedIndex);
    }
  };

  return (
    <button
      type="button"
      className={cn("flex-none w-[85%]", className)}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};
